import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Background_Origin = () => {
  return (
    <div className="tutorial-container">
      {/* Page Title */}
      <h1 className="tutorial-title">📐 Advanced CSS: background-origin</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <code>background-origin</code> property specifies the{" "}
        <b>starting position</b> of a background image within an element. It
        controls whether the background starts from the <b>border box</b>,{" "}
        <b>padding box</b>, or <b>content box</b>.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>border-box</code>: background starts from the outer edge of
            the border.
          </li>
          <li>
            <code>padding-box</code>: background starts inside the border, at
            the padding area.
          </li>
          <li>
            <code>content-box</code>: background starts only where the content
            begins.
          </li>
          <li>
            Works with <code>background-clip</code> for precise control.
          </li>
          <li>
            Often used when adding <b>patterns or gradients</b> to bordered
            elements.
          </li>
        </ul>
      </div>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`/* Default is padding-box */
background-origin: border-box | padding-box | content-box;`}
        />
      </div>

      {/* Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">⚡ Advanced Examples</h2>

        {/* Example 1 */}
        <h3 className="section-subtitle">
          1. border-box (default border included)
        </h3>
        <CodeCopyBlock
          code={`div {
  background-image: url("pattern.png");
  background-origin: border-box;
  border: 10px dashed #f87171;
}`}
        />
        <div
          style={{
            width: "280px",
            height: "120px",
            border: "10px dashed #f87171",
            backgroundImage:
              "url('https://www.transparenttextures.com/patterns/cubes.png')",
            backgroundOrigin: "border-box",
            backgroundSize: "50px 50px",
            marginTop: "0.5rem",
          }}
        />

        {/* Example 2 */}
        <h3 className="section-subtitle">2. padding-box (inside the border)</h3>
        <CodeCopyBlock
          code={`div {
  background-image: url("pattern.png");
  background-origin: padding-box;
  border: 10px solid #60a5fa;
}`}
        />
        <div
          style={{
            width: "280px",
            height: "120px",
            border: "10px solid #60a5fa",
            backgroundImage:
              "url('https://www.transparenttextures.com/patterns/cubes.png')",
            backgroundOrigin: "padding-box",
            backgroundSize: "50px 50px",
            marginTop: "0.5rem",
          }}
        />

        {/* Example 3 */}
        <h3 className="section-subtitle">
          3. content-box (only inside content)
        </h3>
        <CodeCopyBlock
          code={`div {
  background-image: url("pattern.png");
  background-origin: content-box;
  border: 10px solid #34d399;
  padding: 20px;
}`}
        />
        <div
          style={{
            width: "280px",
            height: "120px",
            border: "10px solid #34d399",
            padding: "20px",
            backgroundImage:
              "url('https://www.transparenttextures.com/patterns/cubes.png')",
            backgroundOrigin: "content-box",
            backgroundSize: "50px 50px",
            marginTop: "0.5rem",
          }}
        >
          <p style={{ textAlign: "center", margin: 0, fontWeight: "bold" }}>
            Content Box
          </p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          After mastering <code>background-origin</code>, explore{" "}
          <a href="/css/background-clip" className="learn-more">
            CSS Background Clip
          </a>{" "}
          to control how far the background extends inside the box model.
        </p>
      </div>
    </div>
  );
};

export default Background_Origin;
